'use client'

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react'
import { getInboxItems, markInboxRead, actOnInboxItem } from './actions'
import type { InboxItem } from './actions'

interface InboxContextValue {
  items: InboxItem[]
  unreadCount: number
  loading: boolean
  refresh: () => Promise<void>
  markRead: (itemId: string) => Promise<void>
  actOn: (
    itemId: string,
    action: 'accepted' | 'declined' | 'dismissed',
  ) => Promise<void>
}

const InboxContext = createContext<InboxContextValue | null>(null)

export function InboxProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<InboxItem[]>([])
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      const data = await getInboxItems()
      setItems(data)
    } finally {
      setLoading(false)
    }
  }, [])

  // Poll on mount
  useEffect(() => {
    refresh()
  }, [refresh])

  const markRead = useCallback(async (itemId: string) => {
    await markInboxRead(itemId)
    setItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, status: 'read' as const } : item,
      ),
    )
  }, [])

  const actOn = useCallback(
    async (itemId: string, action: 'accepted' | 'declined' | 'dismissed') => {
      await actOnInboxItem(itemId, action)
      setItems((prev) => prev.filter((item) => item.id !== itemId))
    },
    [],
  )

  const unreadCount = items.filter((i) => i.status === 'unread').length

  return (
    <InboxContext.Provider
      value={{ items, unreadCount, loading, refresh, markRead, actOn }}
    >
      {children}
    </InboxContext.Provider>
  )
}

export function useInbox() {
  const context = useContext(InboxContext)
  if (!context) {
    throw new Error('useInbox must be used within InboxProvider')
  }
  return context
}

/**
 * Optional inbox hook — returns null when InboxProvider is not mounted.
 * Safe to use in components that render before the provider.
 */
export function useOptionalInbox() {
  return useContext(InboxContext)
}
